import { rundeKaufmaennisch2Digits } from "../../models/calc/rundeKaufmaennisch"
import { Position } from "../../models/position"
import { SteuerDetails } from "../../models/rechnung/rechnungsebene/steuerdetails"
import { ZuAbschlag } from "../../models/rechnung/rechnungsebene/zuAbschlag"
import { getSteuerberechnungsOption, isNil } from "../../Utils"

interface GroupedBy {
    type: "position" | "zuschlag" | "abschlag"
    nettobetrag: number | null
    umsatzsteuer: number | null
    umsatzsteuerCode: string
    grund: string
    grundCode: string
}

export function groupBy<T>(array: T[], f: (item: T) => unknown[]): T[][] {
    const groups: Record<string, T[]> = {}
    array.forEach((item) => {
        const group = JSON.stringify(f(item))
        groups[group] = groups[group] || []
        groups[group].push(item)
    })
    return Object.keys(groups).map((group) => groups[group])
}

export const groupByUmsatzsteuer = ({
    positionen,
    rechnungAbschlaege,
    rechnungZuschlaege,
    steuerDetails,
    steuerberechnung,
}: {
    positionen: Position[]
    rechnungAbschlaege: ZuAbschlag[]
    rechnungZuschlaege: ZuAbschlag[]
    steuerDetails: SteuerDetails[]
    steuerberechnung?: string
}): SteuerDetails[] => {
    const mergedSteuerDetails = [
        ...positionen.map(
            (p): GroupedBy => ({
                type: "position",
                nettobetrag: p.gesamtpreisNetto ? Number(p.gesamtpreisNetto) : null,
                umsatzsteuer: !isNil(p.umsatzsteuer) ? Number(p.umsatzsteuer) : null,
                umsatzsteuerCode: p.umsatzsteuerCode,
                grund: "",
                grundCode: "",
            }),
        ),
        ...rechnungZuschlaege.map(
            (p): GroupedBy => ({
                type: "zuschlag",
                nettobetrag: p.zuAbschlaege.nachlassNetto ? Number(p.zuAbschlaege.nachlassNetto) : null,
                umsatzsteuer: !isNil(p.umsatzsteuersatz) ? Number(p.umsatzsteuersatz) : null,
                umsatzsteuerCode: p.umsatzsteuerkategorie,
                grund: "",
                grundCode: "",
            }),
        ),
        ...rechnungAbschlaege.map(
            (p): GroupedBy => ({
                type: "abschlag",
                nettobetrag: p.zuAbschlaege.nachlassNetto ? Number(-p.zuAbschlaege.nachlassNetto) : null,
                umsatzsteuer: !isNil(p.umsatzsteuersatz) ? Number(p.umsatzsteuersatz) : null,
                umsatzsteuerCode: p.umsatzsteuerkategorie,
                grund: "",
                grundCode: "",
            }),
        ),
    ]
    const art = getSteuerberechnungsOption(steuerberechnung)

    return (
        groupBy(mergedSteuerDetails, (item) => [item.umsatzsteuerCode, item.umsatzsteuer])
            .map<SteuerDetails>((g) =>
                g.reduce(
                    (a, b): SteuerDetails => ({
                        ...a,
                        gesamtsummeNetto: b.nettobetrag ? a.gesamtsummeNetto + rundeKaufmaennisch2Digits(b.nettobetrag) : a.gesamtsummeNetto,
                        umsatzsteuerbetrag: berechneUmsatzsteuer(art, a, b),
                        umsatzsteuerCode: b.umsatzsteuerCode,
                        umsatzsteuersatz: b.umsatzsteuer || 0,
                        befreiungsgrund:
                            steuerDetails.find(
                                (sd) => String(sd.umsatzsteuerCode) === String(b.umsatzsteuerCode) && Number(sd.umsatzsteuersatz) === Number(b.umsatzsteuer),
                            )?.befreiungsgrund || "",
                        befreiungsgrundCode:
                            steuerDetails.find(
                                (sd) => String(sd.umsatzsteuerCode) === String(b.umsatzsteuerCode) && Number(sd.umsatzsteuersatz) === Number(b.umsatzsteuer),
                            )?.befreiungsgrundCode || "",
                        toJSON: a.toJSON,
                        notEmpty: a.notEmpty,
                    }),
                    new SteuerDetails(),
                ),
            )
            // TODO: das geht bestimmt irgendwie eleganter ;-)
            .map<SteuerDetails>((merged) => {
                merged.umsatzsteuerbetrag = rundeKaufmaennisch2Digits(
                    art == "P" ? merged.umsatzsteuerbetrag : (merged.gesamtsummeNetto * merged.umsatzsteuersatz) / 100,
                )
                merged.gesamtsummeNetto = rundeKaufmaennisch2Digits(merged.gesamtsummeNetto)
                return merged
            })
    )
}

const berechneUmsatzsteuer = (art: string, steuerdetails: SteuerDetails, groupBy: GroupedBy): number => {
    // Art = P(osition)/G(esamt)
    // Gibt 0 bei Berechung über die Gesamtsumme zurück, Berechnung erfolgt später
    return groupBy.nettobetrag && groupBy.umsatzsteuer
        ? art == "P"
            ? rundeKaufmaennisch2Digits(
                  Number(steuerdetails.umsatzsteuerbetrag + (rundeKaufmaennisch2Digits(groupBy.nettobetrag) * groupBy.umsatzsteuer) / 100),
              )
            : 0
        : steuerdetails.umsatzsteuerbetrag
}
